// /portal/spend — the report a facilities manager screenshots for
// their boss. Headline total per period, prior-equivalent comparison,
// bar chart of sub-buckets, service-line chip filter, invoice list,
// CSV export.
//
// Data: single useQuery over portal_invoices (view extended in
// 20260607070000 to include service_line_id + service_line_name).
// Client-side aggregation only — invoice counts per company are small
// (tens at most), so period switching is instant and we avoid an RPC.
//
// "Spend" basis (documented in the UI too — see caption below):
//   * Excludes invoices in 'draft' or 'void' status (not real spend).
//   * Uses `total` (gross invoiced amount, not amount_paid).
//   * Dated by issue_date, falling back to created_at for any row that
//     never got an explicit issue_date set.
// The page caption echoes this so the client knows what the number is.
//
// Prior-equivalent comparison mirrors the convention from
// RevenueTicker.resolveWindows but with the spend-page's period set
// (day/week/month/quarter/year/ytd). Not extracted/shared because the
// ranges + semantics diverge (jobs-recognized vs invoices-issued); the
// pattern is mirrored, with this comment as the link.

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts'
import {
  startOfDay, endOfDay, startOfWeek, endOfWeek,
  startOfMonth, endOfMonth, startOfQuarter, endOfQuarter,
  startOfYear, endOfYear, subDays, subWeeks, subMonths, subQuarters, subYears,
  format, parseISO, isValid, eachDayOfInterval, eachMonthOfInterval,
  isWithinInterval, differenceInCalendarDays,
} from 'date-fns'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatCurrency, formatDate } from '@/lib/utils'
import { ArrowUpRight, ArrowDownRight, Minus, BarChart2, Download } from 'lucide-react'
import { usePortalTheme } from '@/lib/portalTheme'

// ─── Types ───────────────────────────────────────────────────────────
interface SpendInvoice {
  id: string
  invoice_number: string
  status: string
  total: number | null
  amount_paid: number | null
  amount_due: number | null
  currency: string | null
  issue_date: string | null
  created_at: string | null
  service_line_id: string | null
  service_line_name: string | null
}

type PeriodId = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'ytd'

interface Window { start: Date; end: Date; bucketBy: 'day' | 'month' }

const PERIODS: { id: PeriodId; label: string }[] = [
  { id: 'day',     label: 'Day' },
  { id: 'week',    label: 'Week' },
  { id: 'month',   label: 'Month' },
  { id: 'quarter', label: 'Quarter' },
  { id: 'year',    label: 'Year' },
  { id: 'ytd',     label: 'YTD' },
]

// ─── Window resolution (mirror of RevenueTicker.resolveWindows) ─────
function resolveWindows(period: PeriodId, now: Date): { current: Window; prior: Window } {
  switch (period) {
    case 'day': {
      const s = startOfDay(now)
      return {
        current: { start: s, end: endOfDay(now), bucketBy: 'day' },
        prior:   { start: subDays(s, 1), end: endOfDay(subDays(now, 1)), bucketBy: 'day' },
      }
    }
    case 'week': {
      const s = startOfWeek(now)
      return {
        current: { start: s, end: endOfWeek(now), bucketBy: 'day' },
        prior:   { start: subWeeks(s, 1), end: endOfWeek(subWeeks(now, 1)), bucketBy: 'day' },
      }
    }
    case 'month': {
      const s = startOfMonth(now)
      return {
        current: { start: s, end: endOfMonth(now), bucketBy: 'day' },
        prior:   { start: subMonths(s, 1), end: endOfMonth(subMonths(now, 1)), bucketBy: 'day' },
      }
    }
    case 'quarter': {
      const s = startOfQuarter(now)
      return {
        current: { start: s, end: endOfQuarter(now), bucketBy: 'month' },
        prior:   { start: subQuarters(s, 1), end: endOfQuarter(subQuarters(now, 1)), bucketBy: 'month' },
      }
    }
    case 'year': {
      const s = startOfYear(now)
      return {
        current: { start: s, end: endOfYear(now), bucketBy: 'month' },
        prior:   { start: subYears(s, 1), end: endOfYear(subYears(now, 1)), bucketBy: 'month' },
      }
    }
    case 'ytd': {
      const s = startOfYear(now)
      return {
        current: { start: s, end: now, bucketBy: 'month' },
        prior:   { start: subYears(s, 1), end: subYears(now, 1), bucketBy: 'month' },
      }
    }
  }
}

// Spend basis — single source of truth used by both the headline total
// and the per-bucket aggregation.
const REAL_SPEND_STATUSES = new Set(['sent', 'viewed', 'paid', 'partial', 'overdue'])
function isSpend(inv: SpendInvoice): boolean { return REAL_SPEND_STATUSES.has(inv.status) }
function spendDate(inv: SpendInvoice): Date | null {
  const raw = inv.issue_date || inv.created_at
  if (!raw) return null
  try {
    const d = parseISO(raw)
    return isValid(d) ? d : null
  } catch { return null }
}
function spendAmount(inv: SpendInvoice): number { return Number(inv.total) || 0 }

// ─── Component ───────────────────────────────────────────────────────
const UNCATEGORIZED_KEY = '__uncat__'

export default function PortalSpend() {
  const { portalUser } = useAuth()
  const { resolvedTheme, brandColor } = usePortalTheme()
  const [period, setPeriod] = useState<PeriodId>('month')
  // Service-line filter: Set of ids OR the sentinel. Empty Set = "all".
  const [filterIds, setFilterIds] = useState<Set<string>>(new Set())

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['portal-spend-invoices', portalUser?.company_id],
    queryFn: async () => {
      if (!portalUser?.company_id) return []
      const { data, error } = await supabase
        .from('portal_invoices')
        .select('id, invoice_number, status, total, amount_paid, amount_due, currency, issue_date, created_at, service_line_id, service_line_name')
        .order('issue_date', { ascending: false, nullsFirst: false })
      if (error) throw error
      return (data ?? []) as SpendInvoice[]
    },
    enabled: !!portalUser?.company_id,
  })

  // Distinct service lines present in the data — drives chip rendering
  // (we only show lines that have at least one matching row, plus the
  // Uncategorized chip whenever there's at least one NULL row).
  const availableLines = useMemo(() => {
    const map = new Map<string, string>()
    let hasUncat = false
    for (const inv of invoices) {
      if (!isSpend(inv)) continue
      if (inv.service_line_id && inv.service_line_name) map.set(inv.service_line_id, inv.service_line_name)
      else hasUncat = true
    }
    const out = Array.from(map.entries()).sort(([, a], [, b]) => a.localeCompare(b))
    return { lines: out, hasUncat }
  }, [invoices])

  const now = useMemo(() => new Date(), [])
  const { current, prior } = useMemo(() => resolveWindows(period, now), [period, now])

  // Apply spend basis + service-line filter once for the page.
  const eligible = useMemo(() => invoices.filter(isSpend), [invoices])
  const filtered = useMemo(() => {
    if (filterIds.size === 0) return eligible
    return eligible.filter(inv => {
      const key = inv.service_line_id ?? UNCATEGORIZED_KEY
      return filterIds.has(key)
    })
  }, [eligible, filterIds])

  const inCurrent = useMemo(() => filtered.filter(inv => {
    const d = spendDate(inv)
    return d ? isWithinInterval(d, current) : false
  }), [filtered, current])

  const inPrior = useMemo(() => filtered.filter(inv => {
    const d = spendDate(inv)
    return d ? isWithinInterval(d, prior) : false
  }), [filtered, prior])

  const headline = useMemo(() => inCurrent.reduce((s, inv) => s + spendAmount(inv), 0), [inCurrent])
  const priorTotal = useMemo(() => inPrior.reduce((s, inv) => s + spendAmount(inv), 0), [inPrior])

  const change = useMemo(() => {
    if (priorTotal === 0 && headline === 0) return null
    if (priorTotal === 0) return { kind: 'new' as const, pct: null }
    const pct = (headline - priorTotal) / priorTotal
    return { kind: 'pct' as const, pct }
  }, [headline, priorTotal])

  // Bucket aggregation for the chart. Day period: no chart, render the
  // invoice list directly below — bucketing by hour for a day report
  // would look noisy and the period rarely contains more than 1-2 rows.
  const chartData = useMemo(() => {
    if (period === 'day') return []
    const labels: string[] = []
    const keyFor = current.bucketBy === 'day' ? 'yyyy-MM-dd' : 'yyyy-MM'
    const dispFor = current.bucketBy === 'day' ? 'MMM d' : 'MMM'
    const items = current.bucketBy === 'day'
      ? eachDayOfInterval(current)
      : eachMonthOfInterval(current)
    const buckets = new Map<string, number>()
    for (const d of items) {
      const k = format(d, keyFor)
      labels.push(k)
      buckets.set(k, 0)
    }
    for (const inv of inCurrent) {
      const d = spendDate(inv)
      if (!d) continue
      const k = format(d, keyFor)
      if (buckets.has(k)) buckets.set(k, buckets.get(k)! + spendAmount(inv))
    }
    return Array.from(buckets.entries()).map(([k, v]) => ({
      key: k,
      label: format(parseISO(k + (current.bucketBy === 'day' ? '' : '-01')), dispFor),
      amount: v,
    }))
  }, [period, current, inCurrent])

  const currency = inCurrent[0]?.currency ?? eligible[0]?.currency ?? 'USD'
  const periodDescription = describePeriod(period, current, now)

  // ─── CSV export ───────────────────────────────────────────────────
  const handleExport = () => {
    const filename = `spend-${periodSlug(period)}-${format(now, 'yyyyMMdd')}.csv`
    const header = ['Invoice number', 'Issue date', 'Service line', 'Total', 'Amount paid', 'Amount due', 'Status']
    const rows = inCurrent.map(inv => [
      csvEscape(inv.invoice_number),
      csvEscape(inv.issue_date ?? inv.created_at ?? ''),
      csvEscape(inv.service_line_name ?? 'Uncategorized'),
      String(spendAmount(inv).toFixed(2)),
      String((Number(inv.amount_paid) || 0).toFixed(2)),
      String((Number(inv.amount_due) || 0).toFixed(2)),
      csvEscape(inv.status),
    ])
    const blob = new Blob([[header, ...rows].map(r => r.join(',')).join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; a.click()
    URL.revokeObjectURL(url)
  }

  // ─── Render ───────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Period tabs */}
      <div style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--canvas)', border: '1px solid var(--border-subtle)', borderRadius: 10, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {PERIODS.map(p => (
          <button key={p.id} type="button" onClick={() => setPeriod(p.id)}
            style={{
              padding: '6px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, flexShrink: 0,
              background: period === p.id ? 'var(--surface)' : 'transparent',
              color: period === p.id ? 'var(--ink-primary)' : 'var(--ink-tertiary)',
              boxShadow: period === p.id ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
            }}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Headline */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '14px 16px' }}>
        <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>{periodDescription}</p>
        <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 12 }}>
          <p style={{ fontSize: 28, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>
            {formatCurrency(headline, currency)}
          </p>
          {change && <ChangeIndicator change={change} />}
        </div>
        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '6px 0 0' }}>
          Based on invoiced amounts · excludes drafts and voided invoices
          {change && change.kind === 'pct' && (
            <> · prior {describePeriodForCaption(period)}: {formatCurrency(priorTotal, currency)}</>
          )}
        </p>
      </div>

      {/* Service-line filter */}
      {(availableLines.lines.length > 0 || availableLines.hasUncat) && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <FilterChip label="All" active={filterIds.size === 0} onClick={() => setFilterIds(new Set())} />
          {availableLines.lines.map(([id, name]) => (
            <FilterChip key={id} label={name} active={filterIds.has(id)} onClick={() => setFilterIds(prev => toggleSet(prev, id))} />
          ))}
          {availableLines.hasUncat && (
            <FilterChip label="Uncategorized" active={filterIds.has(UNCATEGORIZED_KEY)} onClick={() => setFilterIds(prev => toggleSet(prev, UNCATEGORIZED_KEY))} />
          )}
        </div>
      )}

      {/* Chart / day-list */}
      {period === 'day' ? null : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 8px 6px', height: 220 }}>
          {/* Key by both resolvedTheme and brandColor: recharts
              memoizes SVG paint attributes, so neither a theme flip nor
              a brand recompute (which changes --accent) propagates live
              to the bars without a re-mount. Brand value typically
              stabilizes on first workspace fetch; this key flips once
              when it arrives. */}
          <ResponsiveContainer key={`${resolvedTheme}-${brandColor ?? 'default'}`} width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 14, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--ink-tertiary)' }} stroke="var(--border-default)" />
              <YAxis tick={{ fontSize: 10, fill: 'var(--ink-tertiary)' }} stroke="var(--border-default)"
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)} />
              <Tooltip contentStyle={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12 }}
                formatter={(v: number) => formatCurrency(v, currency)} />
              <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                {chartData.map((d, i) => <Cell key={i} fill={d.amount > 0 ? 'var(--accent)' : 'var(--border-subtle)'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Invoice list — always rendered (becomes the primary surface on Day). */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
            {inCurrent.length} invoice{inCurrent.length === 1 ? '' : 's'}
          </p>
          {inCurrent.length > 0 && (
            <button type="button" onClick={handleExport}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 7, color: 'var(--ink-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              <Download style={{ width: 11, height: 11 }} />
              Export CSV
            </button>
          )}
        </div>

        {isLoading ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', textAlign: 'center', padding: '20px 0' }}>Loading…</p>
        ) : inCurrent.length === 0 ? (
          <div style={{ padding: '28px 16px', textAlign: 'center' }}>
            <BarChart2 style={{ width: 26, height: 26, color: 'var(--ink-tertiary)', margin: '0 auto 10px', display: 'block' }} />
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 2px' }}>No spend in this period</p>
            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>Switch period or clear filters to see more.</p>
          </div>
        ) : (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden' }}>
            {inCurrent.map((inv, i) => (
              <InvoiceRow key={inv.id} inv={inv} first={i === 0} currency={currency} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Subcomponents ──────────────────────────────────────────────────
function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        padding: '6px 12px', borderRadius: 100,
        fontSize: 11, fontWeight: 600, cursor: 'pointer',
        background: active ? 'var(--accent-dim)' : 'transparent',
        color: active ? 'var(--accent-text)' : 'var(--ink-tertiary)',
        border: `1px solid ${active ? 'var(--accent-border)' : 'var(--border-subtle)'}`,
      }}>
      {label}
    </button>
  )
}

function ChangeIndicator({ change }: { change: { kind: 'pct'; pct: number } | { kind: 'new'; pct: null } }) {
  if (change.kind === 'new') {
    return <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>new period</span>
  }
  const up = change.pct > 0
  const flat = Math.abs(change.pct) < 0.005
  const color = flat ? 'var(--ink-tertiary)' : up ? 'var(--positive)' : 'var(--danger)'
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, fontWeight: 700, color }}>
      <Icon style={{ width: 12, height: 12 }} />
      {Math.abs(change.pct * 100).toFixed(1)}%
    </span>
  )
}

function InvoiceRow({ inv, first, currency }: { inv: SpendInvoice; first: boolean; currency: string }) {
  return (
    <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, borderTop: first ? 'none' : '1px solid var(--border-subtle)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, color: 'var(--ink-primary)' }}>{inv.invoice_number}</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 100, background: STATUS_TINT[inv.status]?.bg ?? 'var(--hover-bg)', color: STATUS_TINT[inv.status]?.color ?? 'var(--ink-tertiary)', border: `1px solid ${STATUS_TINT[inv.status]?.border ?? 'var(--border-subtle)'}`, textTransform: 'capitalize' }}>
            {inv.status}
          </span>
        </div>
        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0' }}>
          {inv.service_line_name ?? 'Uncategorized'}
          {inv.issue_date && <> · {formatDate(inv.issue_date)}</>}
        </p>
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', whiteSpace: 'nowrap' }}>
        {formatCurrency(spendAmount(inv), inv.currency ?? currency)}
      </span>
    </div>
  )
}

const STATUS_TINT: Record<string, { color: string; bg: string; border: string }> = {
  sent:    { color: 'var(--info)',     bg: 'var(--info-dim)',     border: 'var(--info-border)' },
  viewed:  { color: 'var(--accent-text)', bg: 'var(--accent-dim)', border: 'var(--accent-border)' },
  paid:    { color: 'var(--positive)', bg: 'var(--positive-dim)', border: 'var(--positive-border)' },
  partial: { color: 'var(--warning)',  bg: 'var(--warning-dim)',  border: 'var(--warning-border)' },
  overdue: { color: 'var(--danger)',   bg: 'var(--danger-dim)',   border: 'var(--danger-border)' },
}

// ─── Helpers ─────────────────────────────────────────────────────────
function toggleSet<T>(s: Set<T>, v: T): Set<T> {
  const next = new Set(s)
  if (next.has(v)) next.delete(v)
  else next.add(v)
  return next
}

function describePeriod(period: PeriodId, current: Window, now: Date): string {
  switch (period) {
    case 'day': return format(now, 'EEEE, MMM d, yyyy')
    case 'week': return `Week of ${format(current.start, 'MMM d')}`
    case 'month': return format(current.start, 'MMMM yyyy')
    case 'quarter': return `Q${Math.floor(current.start.getMonth() / 3) + 1} ${format(current.start, 'yyyy')}`
    case 'year': return format(current.start, 'yyyy')
    case 'ytd': return `Year to date · ${differenceInCalendarDays(now, current.start) + 1} days`
  }
}

function describePeriodForCaption(period: PeriodId): string {
  return ({ day: 'day', week: 'week', month: 'month', quarter: 'quarter', year: 'year', ytd: 'YTD' } as const)[period]
}

function periodSlug(period: PeriodId): string {
  // Stable filename slug for the CSV export.
  return ({ day: 'today', week: 'wtd', month: 'mtd', quarter: 'qtd', year: 'ytd', ytd: 'ytd' } as const)[period]
}

function csvEscape(s: string): string {
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}
